/**
 * /tools/identity-gap-reassessment?clientId=X — STP-118.
 *
 * Same shape as `/tools/kpi-pre-assessment` (STP-022). Each submission
 * appends a new `kpi_baselines` row — the table is append-only by design.
 * The chronological order distinguishes the Step-0 baseline from this
 * Step-9 / 90-day re-assessment.
 *
 * Pilot pragmatism: this route does NOT pre-load the prior baseline
 * scores — it captures a fresh re-rating. Coach-side comparison
 * surfaces in a future R1.x dashboard pass.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

type ScoreKey = 'energyScore' | 'workScore' | 'loveScore' | 'identityGapScore';

interface FormState {
  energyScore: number | null;
  workScore: number | null;
  loveScore: number | null;
  identityGapScore: number | null;
  energyGapText: string;
  workGapText: string;
  loveGapText: string;
  corePainText: string;
}

const DEFAULT_FORM: FormState = {
  energyScore: null,
  workScore: null,
  loveScore: null,
  identityGapScore: null,
  energyGapText: '',
  workGapText: '',
  loveGapText: '',
  corePainText: '',
};

export function IdentityGapReassessmentPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(
          accessToken,
          clientId,
          'identity-gap-reassessment',
        );
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (typeof p.energyScore === 'number') next.energyScore = p.energyScore;
          if (typeof p.workScore === 'number') next.workScore = p.workScore;
          if (typeof p.loveScore === 'number') next.loveScore = p.loveScore;
          if (typeof p.identityGapScore === 'number') next.identityGapScore = p.identityGapScore;
          if (typeof p.energyGapText === 'string') next.energyGapText = p.energyGapText;
          if (typeof p.workGapText === 'string') next.workGapText = p.workGapText;
          if (typeof p.loveGapText === 'string') next.loveGapText = p.loveGapText;
          if (typeof p.corePainText === 'string') next.corePainText = p.corePainText;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const allScored =
    form.energyScore !== null &&
    form.workScore !== null &&
    form.loveScore !== null &&
    form.identityGapScore !== null;
  const canSubmit = !submitting && hydrated && allScored;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.identityGapReassessment.errors.missingClient'));
      return;
    }
    if (!allScored) return;
    // After the allScored guard, all four scores are non-null. Capture
    // them in const-narrowed locals so TS keeps the `number` type without
    // tripping the `non-nullable-type-assertion-style` lint rule.
    const energy = form.energyScore;
    const work = form.workScore;
    const love = form.loveScore;
    const idGap = form.identityGapScore;
    if (energy === null || work === null || love === null || idGap === null) return;
    setSubmitting(true);
    const payload = {
      energyScore: energy,
      workScore: work,
      loveScore: love,
      identityGapScore: idGap,
      ...(form.energyGapText.trim() ? { energyGapText: form.energyGapText.trim() } : {}),
      ...(form.workGapText.trim() ? { workGapText: form.workGapText.trim() } : {}),
      ...(form.loveGapText.trim() ? { loveGapText: form.loveGapText.trim() } : {}),
      ...(form.corePainText.trim() ? { corePainText: form.corePainText.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'identity-gap-reassessment',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(
          cause.envelope.message || t('tools.identityGapReassessment.errors.generic'),
        );
      } else {
        setSubmitError(t('tools.identityGapReassessment.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="identity-gap-reassessment-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.identityGapReassessment.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.identityGapReassessment.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.identityGapReassessment.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.identityGapReassessment.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.identityGapReassessment.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {(
          [
            { key: 'energyScore', textKey: 'energyGapText' },
            { key: 'workScore', textKey: 'workGapText' },
            { key: 'loveScore', textKey: 'loveGapText' },
            { key: 'identityGapScore', textKey: null },
          ] as { key: ScoreKey; textKey: 'energyGapText' | 'workGapText' | 'loveGapText' | null }[]
        ).map(({ key, textKey }) => (
          <article key={key} className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
            <p className="font-mono text-xs uppercase tracking-wide text-gray-1 mb-s2">
              {t(`tools.identityGapReassessment.fields.${key}`)}
            </p>
            <div role="radiogroup" aria-label={t(`tools.identityGapReassessment.fields.${key}`)} className="flex gap-s2 flex-wrap">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                <button
                  key={score}
                  type="button"
                  role="radio"
                  aria-checked={form[key] === score}
                  onClick={() => setForm((prev) => ({ ...prev, [key]: score }))}
                  data-testid={`identity-gap-${key}-${score}`}
                  className={
                    form[key] === score
                      ? 'w-9 h-9 rounded-md bg-flame text-bone font-medium'
                      : 'w-9 h-9 rounded-md border border-bone-3 text-gray-2 hover:text-ink'
                  }
                >
                  {score}
                </button>
              ))}
            </div>
            {textKey && (
              <textarea
                value={form[textKey]}
                onChange={(e) => setForm((prev) => ({ ...prev, [textKey]: e.target.value }))}
                placeholder={t(`tools.identityGapReassessment.placeholders.${textKey}`)}
                className="input-field mt-s3 min-h-20 w-full"
                data-testid={`identity-gap-${textKey}`}
                rows={3}
              />
            )}
          </article>
        ))}

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.identityGapReassessment.fields.corePainText')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.identityGapReassessment.optional')}
              </span>
            </span>
            <textarea
              value={form.corePainText}
              onChange={(e) => setForm((prev) => ({ ...prev, corePainText: e.target.value }))}
              placeholder={t('tools.identityGapReassessment.placeholders.corePainText')}
              className="input-field mt-s2 min-h-20"
              data-testid="identity-gap-core-pain"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="identity-gap-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="identity-gap-saved-at">
            ✓ {t('tools.identityGapReassessment.savedAt', {
              when: savedAt.slice(0, 19).replace('T', ' '),
            })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting
            ? t('tools.identityGapReassessment.submitting')
            : t('tools.identityGapReassessment.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
